
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button"; // Import Button
import { useAuth } from "@/context/AuthContext"; // Import useAuth
import { Separator } from "@/components/ui/separator"; // Import Separator
import { Star, Users, TrendingUp, Shield } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

// ⚙️ FEATURE FLAG: Set to true to enable sign-ups, false to disable
// This should match the setting in RegisterForm.tsx
const SIGNUPS_ENABLED = true;

interface AuthLayoutProps {
  title: string;
  children: React.ReactNode;
  isSignUp?: boolean;
  hideGoogleAuth?: boolean;
}

// Google Logo for Light Mode
const GoogleLogoLight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Google Logo for Dark Mode (with darker backgrounds and better contrast)
const GoogleLogoDark = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#5A9BF5"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#4CAF50"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FFD54F"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#FF7043"/>
  </svg>
);

const AuthLayout: React.FC<AuthLayoutProps> = ({ title, children, isSignUp = false, hideGoogleAuth = false }) => {
  const { signInWithGoogle, signUpWithGoogle } = useAuth(); // Get both functions from context
  const { resolvedTheme } = useTheme(); // Get the current theme
  
  const handleGoogleAuth = isSignUp ? signUpWithGoogle : signInWithGoogle;

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row">
      {/* Auth Form Section */}
      <div className="w-full lg:w-2/5 bg-white dark:bg-background p-4 sm:p-6 lg:p-8 relative flex-shrink-0">
        {/* Logo in top left corner */}
        <div className="absolute top-0 left-0 pl-3 pt-2.5">
          <Link to="/" className="flex-shrink-0 flex items-center">
            <img src="/media/tag-innovation-logo.png" alt="Tag Innovation Logo" className="size-6" />
          </Link>
        </div>
        
        <div className="w-full max-w-sm mx-auto pt-12 sm:pt-16 space-y-6 min-h-full flex flex-col justify-center">
          <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-foreground">
            {title}
          </h2>
          
          {children}

          {/* Divider and Google Button - hidden for password reset */}
          {!hideGoogleAuth && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-background text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full flex items-center justify-center gap-2 h-11"
                onClick={handleGoogleAuth}
                disabled={isSignUp && !SIGNUPS_ENABLED}
              >
                {resolvedTheme === 'dark' ? <GoogleLogoDark /> : <GoogleLogoLight />}
                {isSignUp && !SIGNUPS_ENABLED ? "Google Sign-up Disabled" : "Google"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Welcome UI Section - Hidden on mobile, shown on desktop */}
      <div className="hidden lg:flex w-full lg:w-3/5 bg-muted items-center justify-center p-8">
        <div className="max-w-lg text-center space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground">
              Welcome to Tag-Inn
            </h1>
            <p className="text-xl text-muted-foreground">
              Transform your business with our comprehensive management platform
            </p>
          </div>

          <div className="grid gap-6">
            <div className="flex items-center space-x-4 p-4 bg-card rounded-lg shadow-sm">
              <div className="bg-blue-100 p-3 rounded-full">
                <Star className="h-6 w-6 text-blue-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-foreground">Review Management</h3>
                <p className="text-sm text-muted-foreground">Collect and manage customer reviews effortlessly</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-card rounded-lg shadow-sm">
              <div className="bg-green-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-foreground">Employee Portal</h3>
                <p className="text-sm text-muted-foreground">Streamline team management and scheduling</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-card rounded-lg shadow-sm">
              <div className="bg-primary/10 p-3 rounded-full">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-foreground">Business Analytics</h3>
                <p className="text-sm text-muted-foreground">Get insights to grow your business</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-card rounded-lg shadow-sm">
              <div className="bg-orange-100 p-3 rounded-full">
                <Shield className="h-6 w-6 text-orange-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-foreground">Secure & Reliable</h3>
                <p className="text-sm text-muted-foreground">Your data is protected with enterprise security</p>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <p className="text-sm text-muted-foreground">
              Join thousands of businesses already using Tag-Inn
            </p>
          </div>
        </div>
      </div>

      {/* Mobile welcome message - shown only on mobile below the form */}
      <div className="lg:hidden bg-muted p-6 text-center">
        <h2 className="text-xl font-semibold text-foreground mb-2">
          Welcome to Tag-Inn
        </h2>
        <p className="text-sm text-muted-foreground">
          Transform your business with comprehensive management tools
        </p>
      </div>
    </div>
  );
};

export default AuthLayout;
